import { useState, useCallback } from 'react';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';

async function hashText(text, algo) {
  var encoder = new TextEncoder();
  var data = encoder.encode(text);
  var buffer = await crypto.subtle.digest(algo, data);
  var array = Array.from(new Uint8Array(buffer));
  return array.map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
}

function HashGenerator() {
  const [input, setInput] = useState('');
  const [hashes, setHashes] = useState({ sha1: '', sha256: '', sha384: '', sha512: '' });
  const [copied, setCopied] = useState('');

  var generate = useCallback(async function () {
    if (!input) { setHashes({ sha1: '', sha256: '', sha384: '', sha512: '' }); return; }
    var results = {};
    results.sha1 = await hashText(input, 'SHA-1');
    results.sha256 = await hashText(input, 'SHA-256');
    results.sha384 = await hashText(input, 'SHA-384');
    results.sha512 = await hashText(input, 'SHA-512');
    setHashes(results);
  }, [input]);

  var copy = function (text, label) {
    navigator.clipboard.writeText(text).then(function () { setCopied(label); setTimeout(function () { setCopied(''); }, 2000); });
  };

  var algos = [
    { key: 'sha1', label: 'SHA-1' },
    { key: 'sha256', label: 'SHA-256' },
    { key: 'sha384', label: 'SHA-384' },
    { key: 'sha512', label: 'SHA-512' },
  ];

  return (
    <div className="page-prettifier">
      <SEO title="Hash Generator - SHA-1, SHA-256, SHA-512" description="Free online hash generator. Generate SHA-1, SHA-256, SHA-384, and SHA-512 hashes from any text instantly in your browser." path="/hash-generator" />
      <div className="container">
        <div className="tool-header">
          <h1>Hash Generator</h1>
          <p>Generate cryptographic hashes from any text. All processing happens in your browser.</p>
        </div>
        <AdUnit />
        <div className="editor-pane" style={{ marginBottom: '1rem' }}>
          <div className="pane-header"><span>Input Text</span></div>
          <textarea className="editor-textarea" value={input} onChange={function (e) { setInput(e.target.value); }} placeholder="Enter text to hash..." spellCheck={false} aria-label="Text to hash" style={{ minHeight: '120px' }} />
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <button onClick={generate} className="btn btn-primary" type="button">Generate Hashes</button>
          <button onClick={function () { setInput(''); setHashes({ sha1: '', sha256: '', sha384: '', sha512: '' }); }} className="btn btn-secondary" type="button" style={{ marginLeft: '0.5rem' }}>Clear</button>
        </div>
        <div className="hash-results">
          {algos.map(function (a) {
            return (
              <div key={a.key} className="hash-row">
                <span className="hash-label">{a.label}</span>
                <input type="text" className="hash-value" value={hashes[a.key]} readOnly aria-label={a.label + ' hash'} />
                <button onClick={function () { copy(hashes[a.key], a.key); }} className="copy-btn" type="button" disabled={!hashes[a.key]}>{copied === a.key ? '✓' : 'Copy'}</button>
              </div>
            );
          })}
        </div>
        <section className="tool-guide">
          <h2>What is a Hash?</h2>
          <p>A hash is a fixed-length string generated from any input text. The same input always produces the same hash, but you cannot reverse a hash back to the original text. Hashes are used for verifying file integrity, storing passwords securely, and digital signatures. SHA-256 is the most commonly used algorithm today.</p>
        </section>
      </div>
    </div>
  );
}

export default HashGenerator;
